import React, { useState } from 'react';
import { Row } from '@tanstack/react-table';
import { MoreVertical } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { IamData } from '../../services/user-service';
import { EditIamProfileDetails } from 'features/profile/component/modals/edit-iam-profile-details/edit-iam-profile-details';
import { Dialog } from 'components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ROLES } from 'constant/roles';

/**
 * Renders the actions for a row in the IAM (Identity and Access Management) table.
 *
 * This component provides a dropdown menu with actions that can be performed on a user,
 * such as viewing details, editing the profile, resetting the password, deactivating the user,
 * or resending an activation link if the user is inactive.
 *
 * Features:
 * - Displays a dropdown with context-sensitive actions based on the user's status
 * - Allows opening an "Edit Profile" modal for user details editing
 * - Supports password reset, viewing user details, and resending activation links
 * - Prevents deactivating an already inactive user (disabled action)
 *
 * @param {DataTableRowActionsProps} props - The props for configuring the row actions
 * @param {Row<IamData>} props.row - The row data for the user, containing user information
 * @param {function} props.onViewDetails - Callback function triggered when the "View Details" action is clicked
 * @param {function} props.onResetPassword - Callback function triggered when the "Reset Password" action is clicked
 * @param {function} [props.onResendActivation] - Optional callback function triggered when the "Resend Activation" action is clicked
 *
 * @returns {JSX.Element} - The rendered row actions dropdown and the edit modal if opened
 *
 * @example
 * <DataTableRowActions
 *   row={userRow}
 *   onViewDetails={(user) => console.log('Viewing details for:', user)}
 *   onResetPassword={(user) => console.log('Resetting password for:', user)}
 *   onResendActivation={(user) => console.log('Resending activation for:', user)}
 * />
 */

interface DataTableRowActionsProps {
  row: Row<IamData>;
  onViewDetails: (user: IamData) => void;
  onResetPassword: (user: IamData) => void;
  onResendActivation?: (user: IamData) => void;
  onDeactivateUser?: (user: IamData) => void;
  onChangePlan?: (user: IamData) => void;
  onCancelSubscription?: (user: IamData) => void;
}

export function DataTableRowActions({
  row,
  onViewDetails,
  onResetPassword,
  onResendActivation,
  onDeactivateUser,
  onChangePlan,
  onCancelSubscription,
}: Readonly<DataTableRowActionsProps>) {
  const { t } = useTranslation();
  const user = row.original;
  const currentUser = useCommonStore((state) => state.user);
  const isSuperAdmin = useCommonStore((state) => state.isSuperAdmin);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Check if the current user is trying to deactivate themselves
  const isCurrentUser = currentUser?.id === user.itemId;

  const handleItemClick = (action: (user: IamData) => void, e: React.MouseEvent) => {
    e.stopPropagation();
    action(user);
  };

  const handleDisabledClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  // Check if the user has org_user role
  const hasOnlyOrgUserRole = user.roles?.includes(ROLES.ORG_USER) && !user.roles.includes(ROLES.ORG_ADMIN);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => handleItemClick(onViewDetails, e)}>
            {t('VIEW_DETAILS')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleEditClick}>{t('EDIT_PROFILE')}</DropdownMenuItem>
          {user.active ? (
            <>
              <DropdownMenuItem onClick={(e) => handleItemClick(onResetPassword, e)}>
                {t('RESET_PASSWORD')}
              </DropdownMenuItem>
              {onChangePlan && isSuperAdmin && (
                <DropdownMenuItem
                  onClick={hasOnlyOrgUserRole ? handleDisabledClick : (e) => handleItemClick(onChangePlan, e)}
                  className={hasOnlyOrgUserRole ? "opacity-50 cursor-not-allowed" : ""}
                  title={hasOnlyOrgUserRole ? 'This action is not available for org_user role' : ''}
                >
                  {t('CHANGE_PLAN_FOR_USER')}
                </DropdownMenuItem>
              )}
              {onCancelSubscription && isSuperAdmin && (
                <DropdownMenuItem
                  onClick={hasOnlyOrgUserRole ? handleDisabledClick : (e) => handleItemClick(onCancelSubscription, e)}
                  title={hasOnlyOrgUserRole ? 'This action is not available for org_user role' : ''}
                  className={hasOnlyOrgUserRole ? "text-error opacity-50 cursor-not-allowed" : "text-error"}
                >
                  {t('CANCEL_SUBSCRIPTION')}
                </DropdownMenuItem>
              )}
              {onDeactivateUser && !isCurrentUser && (
                <DropdownMenuItem
                  onClick={(e) => handleItemClick(onDeactivateUser, e)}
                  className="text-error"
                >
                  {t('DEACTIVATE_USER')}
                </DropdownMenuItem>
              )}
            </>
          ) : (
            <>
              {onResendActivation && (
                <DropdownMenuItem onClick={(e) => handleItemClick(onResendActivation, e)}>
                  {t('RESEND_ACTIVATION_LINK')}
                </DropdownMenuItem>
              )}
              {onChangePlan && isSuperAdmin && (
                <DropdownMenuItem
                  onClick={hasOnlyOrgUserRole ? handleDisabledClick : (e) => handleItemClick(onChangePlan, e)}
                  className={hasOnlyOrgUserRole ? "opacity-50 cursor-not-allowed" : ""}
                  title={hasOnlyOrgUserRole ? 'This action is not available for org_user role' : ''}
                >
                  {t('CHANGE_PLAN_FOR_USER')}
                </DropdownMenuItem>
              )}
              {onCancelSubscription && isSuperAdmin && (
                <DropdownMenuItem
                  onClick={hasOnlyOrgUserRole ? handleDisabledClick : (e) => handleItemClick(onCancelSubscription, e)}
                  className={hasOnlyOrgUserRole ? "text-error opacity-50 cursor-not-allowed" : "text-error"}
                  title={hasOnlyOrgUserRole ? 'This action is not available for org_user role' : ''}
                >
                  {t('CANCEL_SUBSCRIPTION')}
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        {isEditModalOpen && (
          <EditIamProfileDetails
            userInfo={{
              ...user,
              lastName: user.lastName || '',
              profileImageUrl: user.profileImageUrl || '',
            }}
            onClose={handleEditModalClose}
          />
        )}
      </Dialog>
    </>
  );
}
